import CardDashboardService from 'components/atoms/card_dashboard_service';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ApplicationState } from 'store';
import { CalendarEvent, ManagementState } from 'store/modules/management_store/types';
import ImgSemGerenciar from 'assets/images/sem_gerenciar_documento.png';

import { Doughnut } from 'react-chartjs-2';
import { ProfileState } from 'store/modules/profile_store/types';
import { getCalendarEvents, getManagementSummary } from 'store/modules/management_store/actions';
import companyId from 'services/company_id';
import tabId from 'services/tabId';
import CalendarDashboardNew from 'components/molecules/calendar_dashboard_new';
import createTrack from 'services/actions/tracker_actions';
import { totalOf } from 'hooks/useRepresentantDashboard';
import { ManagedMetric, useRepresentantManaged } from 'hooks/useRepresentantManaged';
import {
  CompanyCalendarEvent,
  MANAGED_CHART_METRICS,
  useRepresentantManagedOverview,
} from 'hooks/useRepresentantManagedOverview';
import { getSelectedRepresented } from 'services/selected_companies';
import { Container, Cards } from './styles';

const WINDOW_DAYS_HALF = 60;

const CLASS_BY_TYPE: Record<CalendarEvent['type'], string> = {
  bidding: 'bg-dark text-white',
  task: 'bg-info text-white',
  progress: 'bg-warning text-dark',
};

const ManagedBiddingsSession = () => {
  const dispatch = useDispatch();
  const [loadedRange, setLoadedRange] = useState<{ start: Date; end: Date } | null>(null);
  const inFlightRangeRef = useRef<{ start: Date; end: Date } | null>(null);

  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const companies = useMemo(
    () => (profile.representant ? getSelectedRepresented(profile) : []),
    [profile],
  );
  const companiesKey = companies.map(({ id }) => id).join(',');
  const managed = useRepresentantManaged(companies, management.filtered_by);
  const overview = useRepresentantManagedOverview(companies, management.filtered_by);
  const multi = managed.enabled;
  const valueOf = (metric: ManagedMetric, single: number) =>
    multi ? totalOf(managed.breakdown[metric]) : single;
  const multiProps = (metric: ManagedMetric, error: string) =>
    multi ? { breakdown: managed.breakdown[metric], error: managed.error ? error : '' } : {};

  const fetchWindow = (centerDate: Date) => {
    const start = moment(centerDate).subtract(WINDOW_DAYS_HALF, 'days').startOf('day');
    const end = moment(centerDate).add(WINDOW_DAYS_HALF, 'days').endOf('day');
    const range = { start: start.toDate(), end: end.toDate() };
    inFlightRangeRef.current = range;
    const result: any = multi
      ? overview.loadEvents(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'))
      : dispatch(
          getCalendarEvents(
            management.filtered_by,
            companyId(profile),
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD'),
          ),
        );
    // Só marca a janela como carregada se a busca tiver sucesso; em caso de falha,
    // loadedRange não é atualizado, para que a navegação na janela tente de novo.
    const settle = (success: boolean) => {
      if (inFlightRangeRef.current === range) inFlightRangeRef.current = null;
      if (success) setLoadedRange(range);
    };
    if (result && typeof result.then === 'function') {
      result
        .then((response: any) => settle(response === true || (!!response && response.status < 400)))
        .catch(() => settle(false));
    } else {
      settle(true);
    }
  };

  useEffect(() => {
    if (!multi) dispatch(getManagementSummary(management.filtered_by, companyId(profile)));
    fetchWindow(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [management.filtered_by, profile.currentCompany[tabId()], multi, companiesKey]);

  const handleActiveStartDateChange = (activeStartDate: Date) => {
    const monthStart = moment(activeStartDate).startOf('month').toDate();
    const monthEnd = moment(activeStartDate).endOf('month').toDate();
    // Considera também a janela em voo, para não disparar uma segunda busca
    // enquanto a primeira ainda não resolveu.
    const covered = inFlightRangeRef.current || loadedRange;
    if (!covered) {
      fetchWindow(activeStartDate);
      return;
    }
    if (monthStart < covered.start || monthEnd > covered.end) {
      fetchWindow(activeStartDate);
    }
  };

  const totalManagedCount = multi
    ? overview.totalManaged
    : management?.management_summary?.total_managed || 0;
  const summaryLoaded = multi ? overview.summariesLoaded : request?.management_summary?.loaded;

  const totalFinishedSum = management?.management_summary?.total_finished
    ? Object.values(management.management_summary.total_finished).reduce(
        (sum, value) => sum + value,
        0,
      )
    : 0;
  const data = {
    labels: ['Favoritadas', 'Gerenciadas', 'Finalizadas', 'Ganhas'],
    datasets: [
      {
        label: '# de Licitações',
        data: multi
          ? MANAGED_CHART_METRICS.map((metric) => totalOf(overview.chart[metric]))
          : [
              management?.management_summary?.total_favorites || 0,
              management?.management_summary?.total_managed || 0,
              totalFinishedSum || 0,
              management.management_summary.total_finished?.winner || 0,
            ],
        backgroundColor: ['#4DA6FF', '#A07AFF', '#FFC36A', '#80B49B'],
        borderColor: ['#FFFFFF'],
        borderWidth: 2,
      },
    ],
  };

  const options: any = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: '#666',
        },
      },
      ...(multi && {
        tooltip: {
          callbacks: {
            afterLabel: (context: any) =>
              overview.chart[MANAGED_CHART_METRICS[context.dataIndex]]
                .filter((item) => item.value !== 0)
                .map(
                  (item) =>
                    `${item.name}: ${
                      item.value === null ? '--' : item.value.toLocaleString('pt-BR')
                    }`,
                ),
          },
        },
      }),
    },
    cutout: '60%',
  };

  const eventsList = useMemo(
    () =>
      (
        (multi ? overview.events : management.calendar_events || []) as Array<
          CalendarEvent & Partial<Pick<CompanyCalendarEvent, 'companyId' | 'company'>>
        >
      ).map((event) => ({
        id: event.id,
        title: event.title || '',
        start: moment(moment.parseZone(event.date).format('YYYY-MM-DDTHH:mm:ss')).toDate(),
        className: CLASS_BY_TYPE[event.type] || 'bg-light text-dark',
        description: event.description || '',
        type: event.type,
        bidding_id: event.bidding_id,
        companyId: event.companyId,
        company: event.company,
      })),
    [multi, overview.events, management.calendar_events],
  );

  return (
    <Container>
      <h4 className="pt-3">Licitações Gerenciadas</h4>
      {totalManagedCount > 0 && (
        <div className="row">
          <Cards className="col-md-2">
            <CardDashboardService
              title="Tarefas do Dia"
              img="pending_document"
              value={valueOf(
                'tarefas_hoje',
                management?.management_summary?.total_tasks?.today || 0,
              )}
              link="/minhas_licitacoes/management"
              active
              tasks="tasks"
              source="management"
              {...multiProps('tarefas_hoje', 'Erro ao carregar tarefas do dia')}
            />
            <CardDashboardService
              title="Tarefas atrasadas"
              img="delayed_document"
              value={valueOf(
                'tarefas_atrasadas',
                management?.management_summary?.total_tasks?.late || 0,
              )}
              link="/minhas_licitacoes/management"
              active
              tasks="tasks"
              source="management"
              {...multiProps('tarefas_atrasadas', 'Erro ao carregar tarefas atrasadas')}
            />
            <CardDashboardService
              title="Novos andamentos"
              img="new_movements"
              value={valueOf(
                'andamentos',
                management?.management_summary?.total_progress?.unread || 0,
              )}
              link="/minhas_licitacoes/management"
              active
              tasks="progress"
              source="management"
              {...multiProps('andamentos', 'Erro ao carregar novos andamentos')}
            />
          </Cards>
          <div className="col-md-3 mt-md-0 mt-3">
            <div className="card-container h-100">
              <div className="row">
                <div className="col">
                  <h5>Suas licitações</h5>
                </div>
                <div className="col text-right">
                  <a
                    href="/minhas_licitacoes/management"
                    onClick={() =>
                      createTrack('Dashboard - Clicou em ver ferramenta Suas licitações')
                    }
                  >
                    Ver
                  </a>
                </div>
              </div>
              <div className="w-100 h-100 d-flex align-items-center">
                <Doughnut data={data} options={options} />
              </div>
            </div>
          </div>
          <div className="col-md-7 px-3">
            <div className="card-container h-100">
              <CalendarDashboardNew
                eventsList={eventsList}
                onActiveStartDateChange={handleActiveStartDateChange}
              />
            </div>
          </div>
        </div>
      )}

      {summaryLoaded && totalManagedCount === 0 && (
        <div className="row px-5">
          <div className="col-md-5 text-right">
            <img src={ImgSemGerenciar} width="190px" alt="Sem pregões monitorados" />
          </div>
          <div className="col-md-7">
            <h4 className="pb-3 mt-4">Você ainda não gerenciou uma licitação</h4>
            <p>
              Acesse <a href="/boletim_web/public/boletins">Boletins de Licitações </a>
              ou
              <a href="/banco_de_dados"> Encontrar Licitações </a>
              para encontrar uma <br /> licitação do seu interesse e gerenciá-la.
            </p>
            <p>Administre suas licitações em um só lugar com o Gerenciar Licitações.</p>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ManagedBiddingsSession;
